import { Button, FormikSelect } from '@pzh-ui/components'
import { Form, Formik } from 'formik'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { useUserGetListUsers } from '@/api/fetchers'
import { AmbitieStaticStatics, ObjectStaticShort } from '@/api/fetchers.schemas'
import { LoaderSpinner } from '@/components/Loader'
import Modal from '@/components/Modal'
import { ModalFooter } from '@/components/Modal/Modal'
import { Model } from '@/config/objects/types'
import { Role } from '@/context/AuthContext'
import useObject from '@/hooks/useObject'
import useModalStore from '@/store/modalStore'
import {
    getStaticDataFilterRoles,
    getStaticDataLabel,
    getStaticDataPropertyKey,
    getStaticDataPropertyRequired,
} from '@/utils/dynamicObject'
import { generateSchema } from '@/validation/objectGeneralInformation'

interface ObjectPersonModalProps {
    model: Model
}

const ObjectPersonModal = ({ model }: ObjectPersonModalProps) => {
    const { objectId } = useParams()

    const activeModal = useModalStore(state => state.activeModal)
    const setActiveModal = useModalStore(state => state.setActiveModal)

    const { staticData } = model
    const { useGetStatic } = model.fetchers

    const { data } =
        useGetStatic?.<AmbitieStaticStatics>(parseInt(objectId!), {
            query: { enabled: !!objectId },
        }) || {}

    const {
        data: users,
        isFetching,
        isLoading: loadingUsers,
    } = useUserGetListUsers(
        { limit: 500 },
        { query: { enabled: activeModal === 'objectGeneralInformation' } }
    )

    /**
     * Format user options
     */
    const getUserOptions = useCallback(
        (filterRoles?: Role[]) =>
            users?.results
                ?.filter(user => filterRoles?.includes(user.Rol))
                ?.map(user => ({
                    label: user.Gebruikersnaam,
                    value: user.UUID,
                })),
        [users]
    )

    const { usePostObjectStatic } = useObject()
    const { mutate } = usePostObjectStatic(() => setActiveModal(null))

    /**
     * Update person
     */
    const handleFormSubmit = (payload: ObjectStaticShort) => {
        mutate({ lineageId: parseInt(objectId!), data: payload })
    }

    if (!!!staticData?.length) return null

    const initialValues = staticData.reduce((acc: ObjectStaticShort, item) => {
        const key = getStaticDataPropertyKey(item)
        const user = data?.[key]

        acc[item] = user?.UUID

        return acc
    }, {})

    return (
        <Modal id="objectGeneralInformation" title="Algemene informatie">
            {!loadingUsers && !isFetching ? (
                <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={initialValues}
                    validationSchema={toFormikValidationSchema(
                        generateSchema(staticData)
                    )}
                    enableReinitialize>
                    {({ isValid, isSubmitting, dirty }) => (
                        <Form>
                            <div className="flex flex-col gap-4">
                                {staticData.map(item => {
                                    const label = getStaticDataLabel(item)
                                    const required =
                                        getStaticDataPropertyRequired(item)
                                    const filterRoles =
                                        getStaticDataFilterRoles(item)

                                    const userOptions =
                                        getUserOptions(filterRoles)

                                    return (
                                        <div key={item}>
                                            <FormikSelect
                                                name={item}
                                                label={label}
                                                placeholder={`Kies een ${label.toLowerCase()}`}
                                                options={userOptions}
                                                noOptionsMessage={({
                                                    inputValue,
                                                }) =>
                                                    !!inputValue &&
                                                    'Geen resultaten gevonden'
                                                }
                                                isClearable={!required}
                                                required={required}
                                                blurInputOnSelect
                                            />
                                        </div>
                                    )
                                })}
                            </div>

                            <ModalFooter className="mt-4">
                                <Button
                                    variant="link"
                                    onPress={() => setActiveModal(null)}>
                                    Annuleren
                                </Button>
                                <Button
                                    variant="cta"
                                    type="submit"
                                    isDisabled={
                                        !isValid || isSubmitting || !dirty
                                    }
                                    isLoading={isSubmitting}>
                                    Opslaan
                                </Button>
                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
            ) : (
                <div>
                    <LoaderSpinner />
                </div>
            )}
        </Modal>
    )
}

export default ObjectPersonModal
